import { X } from "lucide-react";
import * as React from "react";

import { useAuth } from "@/providers";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { CollectionRecord } from "@/shared/domain/collections";

import styles from "./AddToCollection.module.css";
import { CollectionsPicker } from "./CollectionsPicker";
import { CreateForm } from "./CreateForm";
import { SelectedResources } from "./selectedResources";

export type { SelectedResources };

export interface AddToCollectionProps {
  selected: SelectedResources;
  onCancel: () => void;
  onSuccess?: () => void;
}

// ── local display helpers (only used within this file) ─────────────────────────

function selectionLabel(selected: SelectedResources): string {
  const count = selected.items.size;
  switch (selected.type) {
    case "Dataset":
      return `${count} ${count === 1 ? "dataset" : "datasets"} selected`;
    case "File":
      return `${count} ${count === 1 ? "file" : "files"} selected`;
    case "Event":
      return `${count} ${count === 1 ? "event" : "events"} selected`;
    case "Session":
      return `${count} ${count === 1 ? "session" : "sessions"} selected`;
  }
}

// ── sub-components ────────────────────────────────────────────────────────────

interface PickerHeaderProps {
  subtitle: string;
  onClose: () => void;
}

function PickerHeader({ subtitle, onClose }: PickerHeaderProps) {
  return (
    <div className={styles.header}>
      <div>
        <div className={styles.title}>Add to collection</div>
        <div className={styles.subtitle}>{subtitle}</div>
      </div>
      <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
        <X size={14} />
      </button>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export const AddToCollection: React.FC<AddToCollectionProps> = ({
  selected,
  onCancel,
  onSuccess,
}) => {
  const [view, setView] = React.useState<"list" | "create">("list");
  const [chosenRecords, setChosenRecords] = React.useState<
    Map<string, CollectionRecord>
  >(new Map());
  const { currentOrganization } = useAuth();
  const showNotification = useNotification();

  const subtitle =
    view === "create" ? "Create a new collection" : selectionLabel(selected);

  return (
    <div className={styles.panel}>
      <PickerHeader subtitle={subtitle} onClose={onCancel} />
      {view === "list" ? (
        <CollectionsPicker
          selected={selected}
          onCancel={onCancel}
          onSuccess={onSuccess}
          onCreateNew={() => setView("create")}
          chosenRecords={chosenRecords}
          setChosenRecords={setChosenRecords}
        />
      ) : (
        <CreateForm
          onCancel={() => setView("list")}
          onSuccess={() => {
            showNotification("Collection created");
            onSuccess?.();
            onCancel();
          }}
          orgId={currentOrganization?.org_id ?? ""}
          selected={selected}
        />
      )}
    </div>
  );
};
